import React, { useState, useEffect, useMemo } from "react";
import {
  Box,
  TextField,
  Button,
  Grid,
  Paper,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  CircularProgress,
  Tooltip,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import { DataGrid } from "@mui/x-data-grid";
import { LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { useSelector } from "react-redux";
import { toast } from "react-toastify";
import ConfirmDialog from "../components/ConfirmDialog";

import industryApi from "../api/industryApi";

const pageNo = "IndustryPage";
function IndustryPage() {
  //const user = useSelector((state) => state.auth.user);
  const customerInfor = useSelector((state) => state.auth.customerInfor);
  const [industries, setIndustries] = useState([]);
  const [totalRows, setTotalRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [currentIndustry, setCurrentIndustry] = useState(null);
  const [openAddEditModal, setOpenAddEditModal] = useState(false);
  const [formErrors, setFormErrors] = useState({});
  const [keySearch, setKeySearch] = useState("");
  const [paginationModel, setPaginationModel] = useState({
    page: 0,
    pageSize: 20,
  });

  const [confirmDialogState, setConfirmDialogState] = useState({
    open: false,
    title: "",
    content: "",
    onConfirm: () => {},
    onClose: () => {},
  });

  const fetchIndustries = async (page, pageSize) => {
    setLoading(true);
    try {
      const response = await industryApi.search(keySearch, page, pageSize);
      if (response.success) {
        setIndustries(response.data);
        setTotalRows(response.pagination.totalRecords);
      } else {
        setIndustries([]);
      }
    } catch (error) {
      setIndustries([]);
      toast.error(error.message || "Đã xảy ra lỗi khi lấy dữ liệu.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchIndustries(paginationModel.page, paginationModel.pageSize);
  }, []);

  const handleCloseAddEditModal = () => {
    setOpenAddEditModal(false);
    setCurrentIndustry(null);
    setFormErrors({});
    fetchIndustries(paginationModel.page, paginationModel.pageSize);
  };
  const handleEdit = async (industryId) => {
    try {
      const response = await industryApi.getById(industryId);
      if (response.success) {
        setCurrentIndustry(response.data);
        setOpenAddEditModal(true);
      } else {
        toast.error(response.message || "Đã xảy ra lỗi khi lấy dữ liệu.");
      }
    } catch (error) {
      toast.error(error || "Đã xảy ra lỗi khi lấy dữ liệu.");
    }
  };
  const handleSave = async () => {
    try {
      const response = await industryApi.upsert(currentIndustry);
      if (response.success) {
        toast.success("Lưu thành công!");
        handleCloseAddEditModal();
      } else {
        toast.error(response.message || "Lưu thất bại.");
      }
    } catch (error) {
      toast.error(error.message || "Đã xảy ra lỗi khi lưu.");
    }
  };

  const handleDelete = async (industryId) => {
    try {
      const response = await industryApi.delete(industryId);
      if (response.success) {
        toast.success("Xóa thành công!");
        setConfirmDialogState({
          ...confirmDialogState,
          open: false,
        });
        fetchIndustries(paginationModel.page, paginationModel.pageSize);
      } else {
        toast.error(response.message || "Xóa thất bại.");
      }
    } catch (error) {
      toast.error(error.message || "Đã xảy ra lỗi khi xóa.");
    } finally {
    }
  };

  // --- Định nghĩa các cột cho DataGrid ---
  const columns = useMemo(() => {
    let baseColumns = [
      {
        field: "industryGroupName",
        headerName: "Nhóm ngành nghề",
        headerAlign: "center",
        width: 400,
      },
      {
        field: "industryName",
        headerName: "Tên ngành nghề",
        headerAlign: "center",
        flex: 1,
      },
      {
        field: "industryVatRate",
        headerName: "% thuế GTGT",
        headerAlign: "center",
        align: "right",
        width: 150,
      },
      {
        field: "industryPitRate",
        headerName: "% thuế TNCN",
        headerAlign: "center",
        align: "right",
        width: 150,
      },
      {
        field: "industryDescription",
        headerName: "Mô tả",
        headerAlign: "center",
        width: 500,
      },
    ];

    if (customerInfor?.CusId === 1) {
      return [
        ...baseColumns,
        {
          field: "actions",
          headerName: "Chức năng",
          headerAlign: "center",
          type: "actions",
          sortable: false,
          filterable: false,
          renderCell: (params) => (
            <Box>
              <Tooltip title="Sửa">
                <IconButton
                  color="primary"
                  size="small"
                  onClick={() => handleEdit(params.row.industryId)}
                >
                  <EditIcon fontSize="small" />
                </IconButton>
              </Tooltip>
              <Tooltip title="Xoá">
                <IconButton
                  color="error"
                  size="small"
                  onClick={() => {
                    setConfirmDialogState({
                      open: true,
                      title: "Xoá ngành nghề",
                      content: "Bạn có chắc muốn xoá ngành nghề này?",
                      onConfirm: () => handleDelete(params.row.industryId),
                      onClose: () =>
                        setConfirmDialogState({
                          ...confirmDialogState,
                          open: false,
                        }),
                    });
                  }}
                >
                  <DeleteIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            </Box>
          ),
        },
      ];
    }

    return baseColumns;
  }, [customerInfor?.CusId]);

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs}>
      <Box sx={{ p: 2, flexGrow: 1, display: "flex", flexDirection: "column" }}>
        {/* Khung tìm kiếm */}
        <Paper elevation={3} sx={{ p: 2, mb: 3 }}>
          <Grid
            container
            spacing={1}
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
            }}
          >
            <Grid
              sx={{
                display: "flex",
                alignItems: "center",
              }}
            >
              <TextField
                label="Từ khóa"
                type="text"
                fullWidth
                value={keySearch || ""}
                onChange={(e) => setKeySearch(e.target.value)}
                size="small"
                sx={{ mr: 1, minWidth: 400 }}
              ></TextField>
              <Button
                variant="contained"
                color="primary"
                startIcon={<SearchIcon />}
                sx={{ width: 180 }}
                onClick={() => {
                  setPaginationModel({
                    page: 0,
                    pageSize: paginationModel.pageSize,
                  });
                  fetchIndustries(0, paginationModel.pageSize);
                }}
              >
                Tìm kiếm
              </Button>
            </Grid>
            {customerInfor?.CusId === 1 && (
              <Grid>
                <Button
                  variant="contained"
                  color="primary"
                  startIcon={<AddIcon />}
                  onClick={() => {
                    setCurrentIndustry(null);
                    setOpenAddEditModal(true);
                  }}
                >
                  Thêm ngành nghề
                </Button>
              </Grid>
            )}
          </Grid>
        </Paper>

        <Box sx={{ flexGrow: 1, width: "100%" }}>
          <DataGrid
            rows={industries}
            columns={columns}
            pageSizeOptions={[20, 50, 100]}
            disableRowSelectionOnClick
            loading={loading}
            getRowId={(row) => row.industryId}
            showCellVerticalBorder
            showColumnVerticalBorder
            onPaginationModelChange={(newModel) => {
              setPaginationModel(newModel);
              fetchIndustries(newModel.page, newModel.pageSize);
            }}
            paginationModel={paginationModel}
            paginationMode="server"
            rowCount={totalRows}
            slots={{
              noRowsOverlay: () => (
                <Box
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    justifyContent: "center",
                    height: "100%",
                  }}
                >
                  Không có ngành nghề nào được tìm thấy.
                </Box>
              ),
            }}
          />
        </Box>

        {/* Modal Thêm/Sửa vai trò */}
        <Dialog open={openAddEditModal} maxWidth="sm" fullWidth>
          <DialogTitle>
            {currentIndustry ? "Sửa nghành nghề" : "Thêm nghành nghề"}
          </DialogTitle>
          <DialogContent>
            <TextField
              label="Tên ngành nghề"
              type="text"
              fullWidth
              variant="outlined"
              value={currentIndustry?.industryName || ""}
              onChange={(e) =>
                setCurrentIndustry({
                  ...currentIndustry,
                  industryName: e.target.value,
                })
              }
              error={!!formErrors.industryName}
              helperText={formErrors?.industryName || ""}
              sx={{ mt: 1, mb: 1 }}
              size="small"
            />
            <Grid size={12}>
              <Grid size={6}>
                <TextField
                  label="% thuế GTGT"
                  type="number"
                  fullWidth
                  variant="outlined"
                  value={currentIndustry?.industryVatRate || ""}
                  onChange={(e) =>
                    setCurrentIndustry({
                      ...currentIndustry,
                      industryVatRate: e.target.value,
                    })
                  }
                  error={!!formErrors.industryVatRate}
                  helperText={formErrors?.industryVatRate || ""}
                  sx={{ mb: 1 }}
                  size="small"
                />
              </Grid>
              <Grid size={6}>
                <TextField
                  label="% thuế TNCN"
                  type="number"
                  fullWidth
                  variant="outlined"
                  value={currentIndustry?.industryPitRate || ""}
                  onChange={(e) =>
                    setCurrentIndustry({
                      ...currentIndustry,
                      industryPitRate: e.target.value,
                    })
                  }
                  error={!!formErrors.industryPitRate}
                  helperText={formErrors?.industryPitRate || ""}
                  sx={{ mb: 1 }}
                  size="small"
                />
              </Grid>
            </Grid>

            <TextField
              label="Mô tả"
              type="text"
              fullWidth
              variant="outlined"
              value={currentIndustry?.industryDescription || ""}
              onChange={(e) =>
                setCurrentIndustry({
                  ...currentIndustry,
                  industryDescription: e.target.value,
                })
              }
              error={!!formErrors.industryDescription}
              helperText={formErrors.industryDescription}
              sx={{ mb: 1 }}
              size="small"
              multiline
              rows={4}
            />
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCloseAddEditModal} color="primary">
              Hủy
            </Button>
            <Button
              onClick={handleSave}
              variant="contained"
              color="primary"
              disabled={loading}
            >
              {loading ? <CircularProgress size={24} color="inherit" /> : "Lưu"}
            </Button>
          </DialogActions>
        </Dialog>

        <ConfirmDialog
          open={confirmDialogState.open}
          title={confirmDialogState.title}
          content={confirmDialogState.content}
          onConfirm={confirmDialogState.onConfirm}
          onClose={confirmDialogState.onClose}
        />
      </Box>
    </LocalizationProvider>
  );
}

export default IndustryPage;
